import * as React from "react"
import { zodResolver } from "@hookform/resolvers/zod"
import { useMutation, useQueries, useQueryClient } from "@tanstack/react-query"
import { useNavigate } from "@tanstack/react-router"
import { format } from "date-fns"
import Cookies from "js-cookie"
import { useForm } from "react-hook-form"
import { z } from "zod"

import type { ServiceRequestType } from "@/types/billing"
import { getZoneDropdown } from "@/lib/api/billing"
import { getUnassignedMeters } from "@/lib/api/meter"
import { docsRequirements, validIds } from "@/lib/mock-data"
import { serviceRequestSchema } from "@/lib/schema/consumer"
import { cn } from "@/lib/utils"
import { useToast } from "@/hooks/use-toast"
import { Button } from "@/components/ui/button"
import { CalendarSingle } from "@/components/ui/calendar-single"
import { Card } from "@/components/ui/card"
import { DropdownMenuSeparator } from "@/components/ui/dropdown-menu"
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form"
import { Input } from "@/components/ui/input"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { ComboboxSearch } from "@/components/combobox-search"
import { Icons } from "@/components/icons"
import { MapRenderer } from "@/components/map/map-renderer"

import profilePlaceholder from "../assets/photo-placeholder.svg"
import { Checkbox } from "../ui/checkbox"
import { Label } from "../ui/label"
import { Textarea } from "../ui/textarea"

export function ServiceRequestForm({
  editData,
}: {
  editData?: ServiceRequestType
}) {
  const [zoneId, setZoneId] = React.useState<number | undefined>(editData?.zone)
  const client = Cookies.get("user_client_info") || "{}"
  const location = {
    lat: editData?.lat || JSON.parse(client).lat,
    lon: editData?.lon || JSON.parse(client).lon,
  }
  const queryConfigs = [
    {
      queryKey: ["zones"],
      queryFn: getZoneDropdown,
    },
  ]

  const results = useQueries({
    queries: queryConfigs,
  })

  const [zones] = results.map((result) => result.data || [])

  const form = useForm<z.infer<typeof serviceRequestSchema>>({
    resolver: zodResolver(serviceRequestSchema),
    defaultValues: editData
      ? {
          invoiceNo: editData.invoice_no || "",
          applicationDate: editData.application_date || "",

          accountNumber: editData.account_number || "",
          cardNumber: editData.card_number || "",
          fullName: editData.full_name || "",
          mobileNumber: editData.mobile_number?.startsWith("+63")
            ? editData.mobile_number.slice(3)
            : editData.mobile_number || "",
          email: editData.email || "",
          zone: editData.zone || 0,
          category: editData.category || "",
          completeAddress: editData.complete_address || "",

          meterNo: editData.meter_no || "",
          brand: editData.brand || "",
          initialRDG: editData.initial_rdg || "",
          lastRDG: editData.last_rdg || "",

          complain: editData.complain || "",
          remarks: editData.remarks || "",
          actionRequired: editData.action_required || "",

          newMeterNumber: editData.new_meter_number || "",
          newBrand: editData.new_brand || "",
          size: editData.size || "",
          date: editData.date || "",
          requestedBy: editData.requested_by || "",
          preparedBy: editData.prepared_by || "",
          approvedBy: editData.approved_by || "",
        }
      : {
          invoiceNo: "",
          applicationDate: "",

          accountNumber: "",
          cardNumber: "",
          fullName: "",
          mobileNumber: "",
          email: "",
          zone: 0,
          category: "",
          completeAddress: "",

          meterNo: "",
          brand: "",
          initialRDG: "",
          lastRDG: "",

          complain: "",
          remarks: "",
          actionRequired: "",

          newMeterNumber: "",
          newBrand: "",
          size: "",
          date: "",
          requestedBy: "",
          preparedBy: "",
          approvedBy: "",
        },
  })

  async function onSubmit(values: z.infer<typeof serviceRequestSchema>) {
    console.log(values)
  }

  const handlePositionChange = (position: [number, number]) => {
    /* form.setValue("latitude", position[0])
    form.setValue("longitude", position[1]) */
  }

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(onSubmit)}
        className="grid grid-cols-1 gap-4 lg:container"
      >
        <div>
          <div className="flex flex-col gap-4 md:flex-row md:justify-between lg:justify-between">
            <span className="text-2xl font-bold">Service Request Form</span>
            <div className="flex flex-row justify-end gap-4">
              <div>
                <FormField
                  control={form.control}
                  name="invoiceNo"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Invoice No:</FormLabel>
                      <FormControl>
                        <Input placeholder="Invoice No" {...field} />
                      </FormControl>
                    </FormItem>
                  )}
                />
              </div>
              <div>
                <FormField
                  control={form.control}
                  name="applicationDate"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Application Date:</FormLabel>
                      <FormControl>
                        <Input type="date" {...field} />
                      </FormControl>
                    </FormItem>
                  )}
                />
              </div>
            </div>
          </div>
        </div>

        <Card className="space-y-4 p-4">
          {/* Account Number and Card Number */}
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <FormField
              control={form.control}
              name="accountNumber"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Account Number</FormLabel>
                  <FormControl>
                    <Input placeholder="Enter account number..." {...field} />
                  </FormControl>
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="cardNumber"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Card Number</FormLabel>
                  <FormControl>
                    <Input placeholder="Enter card number..." {...field} />
                  </FormControl>
                </FormItem>
              )}
            />
          </div>

          {/* Full Name */}
          <FormField
            control={form.control}
            name="fullName"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Full Name</FormLabel>
                <FormControl>
                  <Input placeholder="Full Name" {...field} />
                </FormControl>
              </FormItem>
            )}
          />

          {/* Zone and Category */}
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <FormField
              control={form.control}
              name="zone"
              render={({ field }) => (
                <FormItem className="flex flex-col justify-center">
                  <FormLabel className="py-1">
                    Zone <span className="text-destructive">*</span>
                  </FormLabel>
                  <FormControl>
                    <ComboboxSearch
                      field={field}
                      options={zones}
                      placeholder="Select zone"
                      onSelectId={(id?: string | number) =>
                        setZoneId(id as number)
                      }
                      idAsValue
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="category"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Category</FormLabel>
                  <FormControl>
                    <select className="w-full rounded-md border p-2" {...field}>
                      <option value="">-------</option>
                      {/* Add options here */}
                    </select>
                  </FormControl>
                </FormItem>
              )}
            />
          </div>

          {/* Complete Address */}
          <FormField
            control={form.control}
            name="completeAddress"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Complete Address</FormLabel>
                <FormControl>
                  <Input placeholder="Complete Address" {...field} />
                </FormControl>
              </FormItem>
            )}
          />

          {/* Meter Details */}
          <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
            <FormField
              control={form.control}
              name="meterNo"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Meter No.</FormLabel>
                  <FormControl>
                    <Input placeholder="Meter No." {...field} />
                  </FormControl>
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="brand"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Brand</FormLabel>
                  <FormControl>
                    <Input placeholder="Brand" {...field} />
                  </FormControl>
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="initialRDG"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Initial RDG</FormLabel>
                  <FormControl>
                    <Input placeholder="Initial RDG" {...field} />
                  </FormControl>
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="lastRDG"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Last RDG</FormLabel>
                  <FormControl>
                    <Input placeholder="Last RDG" {...field} />
                  </FormControl>
                </FormItem>
              )}
            />
          </div>
        </Card>

        <Card className="space-y-4 p-4">
          {/* Complain */}
          <FormField
            control={form.control}
            name="complain"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Complain</FormLabel>
                <FormControl>
                  <Input placeholder="Complain" {...field} />
                </FormControl>
              </FormItem>
            )}
          />

          {/* Remarks */}
          <FormField
            control={form.control}
            name="remarks"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Remarks</FormLabel>
                <FormControl>
                  <Textarea placeholder="Remarks" rows={4} {...field} />
                </FormControl>
              </FormItem>
            )}
          />

          {/* Action Required */}
          <FormField
            control={form.control}
            name="actionRequired"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Action Required</FormLabel>
                <FormControl>
                  <Textarea placeholder="Action Required" rows={4} {...field} />
                </FormControl>
              </FormItem>
            )}
          />
        </Card>

        <Card className="mt-4 space-y-2 p-4">
          <p className="text-lg font-semibold">Meter Information</p>
          <DropdownMenuSeparator />

          <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
            <FormField
              control={form.control}
              name="newMeterNumber"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>New Meter Number</FormLabel>
                  <FormControl>
                    <Input placeholder="New Meter Number" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="brand"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Brand</FormLabel>
                  <FormControl>
                    <Input placeholder="Brand" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="size"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Size</FormLabel>
                  <FormControl>
                    <Input placeholder="Size" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="date"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Date</FormLabel>
                  <FormControl>
                    <Input type="date" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </Card>
        <Card className="space-y-4 p-4">
          {/* View Map */}
          <div className="mt-4">
            <FormLabel>View Map:</FormLabel>
            <div className="overflow-hidden rounded-lg border">
              <MapRenderer
                picker={{
                  lat: location.lat,
                  lng: location.lon,
                }}
                onPositionChange={handlePositionChange}
              />
            </div>
          </div>

          {/* Footer Fields */}
          <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-3">
            <FormField
              control={form.control}
              name="requestedBy"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Requested By</FormLabel>
                  <FormControl>
                    <Input placeholder="Requested By" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="preparedBy"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Prepared By</FormLabel>
                  <FormControl>
                    <Input placeholder="Prepared By" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="approvedBy"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Approved By</FormLabel>
                  <FormControl>
                    <Input placeholder="Approved By" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          {/* Buttons */}
          <div className="mt-6 flex gap-2">
            <Button
              className="w-[150px] bg-gray-500 hover:bg-gray-500"
              type="button"
            >
              Back
            </Button>
            <Button className="w-[150px]" type="submit">
              Print
            </Button>
          </div>
        </Card>
      </form>
    </Form>
  )
}
